import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { PublicWebFormConfigResponse } from '@orion/contracts';
import { Button, Input } from '@orion/ui';
import { ApiError, api } from '../../lib/api-client.js';

interface FormValues {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  companyName?: string;
  jobTitle?: string;
  message?: string;
  website2?: string;
}

function buildSchema(config: PublicWebFormConfigResponse) {
  const shape: Record<string, z.ZodTypeAny> = {
    firstName: z.string().trim().min(1, 'First name is required').max(100),
    lastName: z.string().trim().min(1, 'Last name is required').max(100),
    website2: z.string().max(0).optional(),
  };
  for (const f of config.fields) {
    if (f.key === 'email') {
      shape[f.key] = f.required
        ? z.string().trim().min(1, `${f.label} is required`).email('Enter a valid email')
        : z.union([z.string().trim().email('Enter a valid email'), z.literal('')]).optional();
    } else {
      const base = z.string().trim().max(f.key === 'message' ? 2000 : 150);
      shape[f.key] = f.required ? base.min(1, `${f.label} is required`) : base.optional();
    }
  }
  return z.object(shape);
}

/**
 * The one form-rendering component shared by the standalone `/forms/:key`
 * page and the `/embed/forms/:key` iframe variant — same fields, same
 * validation, same submit + success behaviour either way, so the two only
 * differ in the chrome wrapped around this (docs section 9 "Lead Capture").
 * No session, no CSRF needed: the backend's public routes carry neither
 * guard, so the shared `api` client works as-is for an anonymous visitor.
 */
export function DynamicLeadForm({ publicKey, config }: { publicKey: string; config: PublicWebFormConfigResponse }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const schema = useMemo(() => buildSchema(config), [config]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setError(null);
    const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    try {
      await api.post(`/public/forms/${publicKey}/submit`, {
        ...values,
        utmSource: search?.get('utm_source') ?? undefined,
        utmMedium: search?.get('utm_medium') ?? undefined,
        utmCampaign: search?.get('utm_campaign') ?? undefined,
        utmTerm: search?.get('utm_term') ?? undefined,
        utmContent: search?.get('utm_content') ?? undefined,
        referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
        landingPage: typeof window !== 'undefined' ? window.location.href : undefined,
      });
      setSubmitted(true);
      if (config.redirectUrl) {
        window.setTimeout(() => window.location.assign(config.redirectUrl!), 900);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.title : 'Something went wrong submitting the form. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-success-soft text-success">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <h2 className="text-lg font-semibold text-ink">{config.successHeadline}</h2>
        <p className="text-sm text-text-muted">{config.successMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Input label="First name" error={errors.firstName?.message} {...register('firstName')} />
        <Input label="Last name" error={errors.lastName?.message} {...register('lastName')} />
      </div>

      {config.fields.map((f) =>
        f.key === 'message' ? (
          <label key={f.key} className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            {f.label}
            <textarea
              rows={3}
              className="rounded-control border border-border-strong bg-surface px-3 py-2 text-sm font-normal text-ink"
              {...register('message')}
            />
            {errors.message && <span className="text-xs font-normal text-danger">{errors.message.message}</span>}
          </label>
        ) : (
          <Input
            key={f.key}
            label={f.label}
            type={f.key === 'email' ? 'email' : f.key === 'phone' ? 'tel' : 'text'}
            error={errors[f.key]?.message}
            {...register(f.key)}
          />
        ),
      )}

      {/* Honeypot — real visitors never see or fill this in. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
        <label>
          Leave this field blank
          <input type="text" tabIndex={-1} autoComplete="off" {...register('website2')} />
        </label>
      </div>

      {error && (
        <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
          {error}
        </div>
      )}

      <Button type="submit" size="lg" className="mt-1 w-full" loading={isSubmitting}>
        Submit
      </Button>
    </form>
  );
}
